import {
  type ComponentPropsWithoutRef,
  forwardRef,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
} from 'react';
import { BaseLineLeft } from '@/app/components/icons/BaseLineLeft';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { Label } from '@/app/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/app/components/ui/radio-group';
import { useScrollArrow } from '@/app/hooks';
import { cn } from '@/app/utils';

// 最大显示项数（超过此数量时显示箭头）
const MAX_SHOW_ITEMS = 8;

export interface ToggleOption {
  value: string;
  label: ReactNode;
  description?: ReactNode;
  isVipOption?: boolean;
  badgeEle?: ReactNode;
}

type SettingRadioGroupProps = Omit<
  ComponentPropsWithoutRef<typeof RadioGroup>,
  'type' | 'defaultValue' | 'onValueChange'
> & {
  activeMode?: 'slider' | 'item';
  className?: string;
  optionsWrapperClassName?: string;
  activeOptionClassName?: string;
  value?: string;
  options: ToggleOption[];
  onValueChange: (value: string) => void;
  renderContent?: (option: ToggleOption, isActive: boolean) => ReactNode;
  labelClassName?: string;
};

export const SettingRadioGroup = forwardRef<HTMLDivElement, SettingRadioGroupProps>(
  (
    {
      activeMode = 'slider',
      value,
      className = 'rounded-8 bg-system-white04',
      optionsWrapperClassName,
      activeOptionClassName,
      options,
      onValueChange,
      renderContent,
      labelClassName,
      ...triggerProps
    },
    ref,
  ) => {
    const activeIndex = Math.max(
      0,
      options.findIndex((opt) => value === opt.value),
    );

    const {
      scrollContainerRef,
      isLeftDisabled,
      isRightDisabled,
      checkScrollPosition,
      handleScrollLeft,
      handleScrollRight,
    } = useScrollArrow();

    const showArrow = useMemo(() => {
      return options.length > MAX_SHOW_ITEMS;
    }, [options.length]);

    /**
     * 计算箭头和选项的宽度配置
     * showArrow 为 true 时：固定显示 MAX_SHOW_ITEMS 个选项，左右各一个箭头
     * showArrow 为 false 时：所有选项平分宽度
     */
    const widthConfig = useMemo(() => {
      if (showArrow) {
        return {
          arrow: MAX_SHOW_ITEMS, // 箭头宽度百分比
          items: 100 / MAX_SHOW_ITEMS, // 每个选项的宽度百分比
        };
      }
      // 不显示箭头时，所有选项等宽
      return {
        items: 100 / options.length,
      };
    }, [options.length, showArrow]);

    const isVipActive = options[activeIndex]?.isVipOption;

    const radioGroupId = useId();

    /**
     * 合并的 ref 回调函数，同时处理内部和外部 ref
     */
    const mergedRef = useCallback(
      (node: HTMLDivElement | null) => {
        // 设置内部 ref 用于滚动控制
        (scrollContainerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;

        // 同时设置外部传入的 ref
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }

        // 初始检查滚动位置
        if (node) {
          scrollContainerRef.current?.addEventListener('scroll', checkScrollPosition);
          checkScrollPosition();
        }
      },
      [ref, checkScrollPosition],
    );

    /**
     * 当 value 变化时，如果选中项不完全可见，则滚动到可视区域
     */
    useEffect(() => {
      // 只在需要显示箭头（有滚动）时才执行滚动
      if (!showArrow || activeIndex < 0) {
        return;
      }

      // 使用 requestAnimationFrame 确保 DOM 已经渲染完成
      requestAnimationFrame(() => {
        const container = scrollContainerRef.current;
        if (!container) {
          return;
        }

        // 找到选中项的 DOM 元素（使用属性选择器避免 ID 中的特殊字符问题）
        const elementId = `${radioGroupId}-${value}`;
        const activeElement = container.querySelector(`[id="${elementId}"]`)?.parentElement;
        if (!activeElement) {
          return;
        }

        // 计算元素在滚动内容中的位置（相对于滚动容器的内容区域）
        // offsetLeft 是元素相对于 offsetParent 的位置，对于滚动容器内的元素，就是相对于容器内容的位置
        const elementLeftInContent = activeElement.offsetLeft;
        const elementCenterInContent = elementLeftInContent + activeElement.offsetWidth / 2;

        // 容器中心点在滚动内容中的位置 = 当前滚动位置 + 容器可视宽度的一半
        const containerCenterInContent = container.scrollLeft + container.clientWidth / 2;

        // 计算需要滚动的距离：让元素中心点对齐到容器中心点
        const scrollOffset = elementCenterInContent - containerCenterInContent;

        // 如果偏移超过阈值（比如 5px），则滚动使其居中
        if (Math.abs(scrollOffset) > 5) {
          const targetScrollLeft = container.scrollLeft + scrollOffset;

          container.scrollTo({
            left: targetScrollLeft,
            behavior: 'smooth',
          });
        }
      });
    }, [value, activeIndex, showArrow, radioGroupId]);

    const selectComp = (className?: string) => (
      <RadioGroup
        ref={mergedRef}
        className={cn('group relative flex w-full gap-0', className)}
        value={value}
        onValueChange={onValueChange}
        {...triggerProps}
      >
        {activeMode === 'slider' && (
          <div
            className={cn(
              'absolute top-0 bottom-0 rounded-8 border border-system-white08',
              'pointer-events-none bg-system-white08 transition-transform duration-300 ease-out',
              'after:absolute after:inset-0 after:rounded-8 after:bg-gradient-to-b after:from-[#5263FF] after:via-[#6473FF] after:to-[#7784FF] after:opacity-0 after:transition-opacity after:duration-300 after:ease-out after:content-[""]',
              isVipActive && 'border-none after:opacity-100',
              activeOptionClassName,
            )}
            style={{
              width: `${widthConfig.items}%`,
              transform: `translateX(${activeIndex * 100}%)`,
            }}
          />
        )}
        {options.map((option) => {
          const isActive = value === option.value;
          return (
            <div
              key={`${radioGroupId}-${option.value}`}
              className={cn(
                'relative h-9 flex-shrink-0 bg-transparent text-center text-sm text-system-white',
                optionsWrapperClassName,
                activeMode === 'item' && isActive && activeOptionClassName,
              )}
              style={{
                width: `${widthConfig.items}%`,
              }}
            >
              <RadioGroupItem value={option.value} id={`${radioGroupId}-${option.value}`} className="hidden">
                {renderContent?.(option, isActive) || option.label}
              </RadioGroupItem>
              <Label
                htmlFor={`${radioGroupId}-${option.value}`}
                className={cn(
                  'flex h-full cursor-pointer items-center justify-center p-2 text-sm group-aria-disabled:cursor-not-allowed lg:text-xs',
                  option.isVipOption &&
                    !isActive &&
                    'bg-gradient-to-b from-[#7280FF] via-[#8C97FF] to-[#DADFFF] bg-clip-text font-semibold text-transparent',
                  isActive && 'transition-colors duration-300 ease-out',
                  !isActive && labelClassName,
                )}
              >
                {renderContent?.(option, isActive) || option.label}
              </Label>
              {option?.badgeEle}
            </div>
          );
        })}
      </RadioGroup>
    );

    return showArrow ? (
      <div className={cn('isolate flex', className)}>
        <div
          className={cn(
            'relative flex items-center justify-center rounded-l-8 bg-[#232529] transition-opacity duration-200',
            'after:-right-10 after:absolute after:top-0 after:h-full after:w-10',
            'after:bg-gradient-to-r after:from-[#232529] after:to-transparent',
            'after:pointer-events-none after:z-10 after:content-[""]',
            isLeftDisabled ? 'cursor-not-allowed after:opacity-0' : 'cursor-pointer',
          )}
          style={{ width: `${widthConfig.arrow}%` }}
          onClick={handleScrollLeft}
        >
          <BaseLineLeft className={cn('size-3.5 text-system-white', isLeftDisabled && 'opacity-30')} />
        </div>

        {selectComp('flex-1 overflow-x-auto hide-scrollbar')}

        <div
          className={cn(
            'relative flex items-center justify-center rounded-r-8 bg-[#232529] transition-opacity duration-200',
            'after:-left-10 after:absolute after:top-0 after:h-full after:w-10',
            'after:bg-gradient-to-l after:from-[#232529] after:to-transparent',
            'after:pointer-events-none after:z-10 after:content-[""]',
            isRightDisabled ? 'cursor-not-allowed opacity-30' : 'cursor-pointer hover:opacity-80',
          )}
          style={{ width: `${widthConfig.arrow}%` }}
          onClick={handleScrollRight}
        >
          <BaseLineRight className={cn('size-3.5 text-system-white', isRightDisabled && 'opacity-30')} />
        </div>
      </div>
    ) : (
      selectComp(className)
    );
  },
);
